import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ViewContainerRef
} from '@angular/core';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-datetime-field',
  standalone: false,
  templateUrl: './datetime-field.component.html',
  styleUrls: ['./datetime-field.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager
})
export class DatetimeFieldComponent implements OnChanges, OnDestroy {
  private static nextId = 0;

  @Input() value: Date | null = null;
  @Input() mode: 'datetime' | 'date' | 'time' = 'datetime';
  @Input() placeholder = '';
  @Input() disabled = false;
  @Input() min: Date | null = null;
  @Input() inputId = '';

  @Output() valueChange = new EventEmitter<Date | null>();
  @Output() invalidChange = new EventEmitter<boolean>();

  @ViewChild('origin') origin!: ElementRef<HTMLElement>;
  @ViewChild('panel') panel!: TemplateRef<unknown>;

  text = '';
  invalid = false;
  readonly errorId = `amigo-datetime-error-${DatetimeFieldComponent.nextId++}`;
  panelAnchor = new Date();
  private editing = false;
  private overlayRef: OverlayRef | null = null;
  private closeSub: Subscription | null = null;

  constructor(
    private overlay: Overlay,
    private viewContainerRef: ViewContainerRef
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    if (!changes['value'] && !changes['mode']) {
      return;
    }
    if (this.editing || this.invalid) {
      return;
    }
    const next = this.format(this.validValue);
    if (next !== this.text) {
      this.text = next;
    }
  }

  ngOnDestroy() {
    this.close();
  }

  get formatHint(): string {
    if (this.mode === 'time') {
      return 'TT:MM';
    }
    if (this.mode === 'date') {
      return 'ÅÅÅÅ-MM-DD';
    }
    return 'ÅÅÅÅ-MM-DD TT:MM';
  }

  get activePlaceholder(): string {
    return this.editing ? '' : this.placeholder;
  }

  get guideRest(): string {
    if (!this.editing || this.text.length >= this.formatHint.length) {
      return '';
    }
    return this.formatHint.slice(this.text.length);
  }

  get validValue(): Date | null {
    return this.value instanceof Date && !isNaN(this.value.getTime()) ? this.value : null;
  }

  get hourText(): string {
    return this.pad(this.validValue ? this.validValue.getHours() : 0);
  }

  get minuteText(): string {
    return this.pad(this.validValue ? this.validValue.getMinutes() : 0);
  }

  get isOpen(): boolean {
    return this.overlayRef != null;
  }

  onTextInput(event: Event) {
    this.text = (event.target as HTMLInputElement).value;
    this.invalid = false;
  }

  onTextBlur() {
    this.editing = false;
    const parsed = this.parse(this.text);
    if (parsed === undefined) {
      const wasInvalid = this.invalid;
      this.invalid = false;
      this.text = '';
      if (this.validValue) {
        this.valueChange.emit(null);
      } else if (wasInvalid) {
        this.invalidChange.emit(false);
      }
      return;
    }
    if (parsed === null) {
      this.invalid = true;
      this.invalidChange.emit(true);
      return;
    }
    const wasInvalid = this.invalid;
    this.invalid = false;
    this.text = this.format(parsed);
    if (wasInvalid || !this.sameMinute(parsed, this.validValue)) {
      this.valueChange.emit(parsed);
    } else {
      this.invalidChange.emit(false);
    }
  }

  onTextFocus() {
    this.editing = true;
  }

  toggle() {
    if (this.disabled) {
      return;
    }
    if (this.overlayRef) {
      this.close();
    } else {
      this.open();
    }
  }

  onDay(date: Date | null) {
    if (!date) {
      return;
    }
    const current = this.validValue;
    const next = new Date(date);
    if (this.mode === 'date') {
      next.setHours(0, 0, 0, 0);
    } else {
      next.setHours(current ? current.getHours() : 0, current ? current.getMinutes() : 0, 0, 0);
    }
    this.focusCalendar(next);
    this.emit(next);
    if (this.mode === 'date') {
      this.close();
    }
  }

  stepHour(delta: number) {
    const next = this.baseDate();
    next.setHours((next.getHours() + delta + 24) % 24, next.getMinutes(), 0, 0);
    this.emit(next);
  }

  stepMinute(delta: number) {
    const next = this.baseDate();
    next.setHours(next.getHours(), (next.getMinutes() + delta + 60) % 60, 0, 0);
    this.emit(next);
  }

  setToday() {
    const now = new Date();
    now.setSeconds(0, 0);
    this.focusCalendar(now);
    this.emit(now);
  }

  clear() {
    this.emit(null);
    this.close();
  }

  private open() {
    this.panelAnchor = this.validValue ? new Date(this.validValue) : new Date();
    const positionStrategy = this.overlay.position()
      .flexibleConnectedTo(this.origin)
      .withPositions([
        { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 4 },
        { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -4 },
        { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top', offsetY: 4 }
      ])
      .withPush(true);

    this.overlayRef = this.overlay.create({
      positionStrategy,
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
      hasBackdrop: true,
      backdropClass: 'amigo-datetime-backdrop',
      panelClass: 'amigo-datetime-overlay'
    });
    this.overlayRef.attach(new TemplatePortal(this.panel, this.viewContainerRef));
    this.closeSub = new Subscription();
    this.closeSub.add(this.overlayRef.backdropClick().subscribe(() => this.close()));
    this.closeSub.add(this.overlayRef.keydownEvents().subscribe(event => {
      if (event.key === 'Escape') {
        this.close();
      }
    }));
  }

  private close() {
    this.closeSub?.unsubscribe();
    this.closeSub = null;
    this.overlayRef?.dispose();
    this.overlayRef = null;
  }

  private emit(value: Date | null) {
    this.invalid = false;
    this.editing = false;
    this.text = this.format(this.isRealDate(value) ? value : null);
    this.valueChange.emit(value);
  }

  private parse(raw: string): Date | null | undefined {
    const text = raw.trim();
    if (!text) {
      return undefined;
    }
    if (this.mode === 'time') {
      const match = /^(\d{1,2}):(\d{2})$/.exec(text);
      if (!match) {
        return null;
      }
      const hours = Number(match[1]);
      const minutes = Number(match[2]);
      if (hours > 23 || minutes > 59) {
        return null;
      }
      const date = this.validValue ? new Date(this.validValue) : new Date();
      date.setHours(hours, minutes, 0, 0);
      return date;
    }

    const match = /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{1,2}):(\d{2}))?$/.exec(text);
    if (!match) {
      return null;
    }
    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    const date = new Date(year, month - 1, day);
    if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
      return null;
    }
    if (this.mode === 'date') {
      date.setHours(0, 0, 0, 0);
      return date;
    }
    let hours = this.validValue ? this.validValue.getHours() : 0;
    let minutes = this.validValue ? this.validValue.getMinutes() : 0;
    if (match[4] != null) {
      hours = Number(match[4]);
      minutes = Number(match[5]);
      if (hours > 23 || minutes > 59) {
        return null;
      }
    }
    date.setHours(hours, minutes, 0, 0);
    return date;
  }

  private format(date: Date | null): string {
    if (!date) {
      return '';
    }
    const hours = this.pad(date.getHours());
    const minutes = this.pad(date.getMinutes());
    if (this.mode === 'time') {
      return `${hours}:${minutes}`;
    }
    const day = `${date.getFullYear()}-${this.pad(date.getMonth() + 1)}-${this.pad(date.getDate())}`;
    if (this.mode === 'date') {
      return day;
    }
    return `${day} ${hours}:${minutes}`;
  }

  private sameMinute(left: Date, right: Date | null): boolean {
    if (!right) {
      return false;
    }
    return left.getFullYear() === right.getFullYear()
      && left.getMonth() === right.getMonth()
      && left.getDate() === right.getDate()
      && left.getHours() === right.getHours()
      && left.getMinutes() === right.getMinutes();
  }

  private isRealDate(value: Date | null): value is Date {
    return value instanceof Date && !isNaN(value.getTime());
  }

  private baseDate(): Date {
    const current = this.validValue;
    if (current) {
      return new Date(current);
    }
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    return date;
  }

  private focusCalendar(date: Date) {
    const anchor = this.panelAnchor;
    if (anchor.getFullYear() !== date.getFullYear() || anchor.getMonth() !== date.getMonth()) {
      this.panelAnchor = new Date(date);
    }
  }

  private pad(value: number): string {
    return value.toString().padStart(2, '0');
  }
}
